import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFinancialYears,
  getFrameworks,
  getIndustries,
  getGhgDatabases,
  getKeywords,
  getModules,
  getUnitMaster,
} from "../kpiGridApi";
import { TAG_PATH_KEYS } from "./options";
import { withModulePillar } from "./helpers";
import { isModuleKeyword, modulePillarsOf, normalizeTagText } from "../../../../_constants/esgPillars";


const normalizeUnits = (units) => units.map((unit) => ({ ...unit, id: Number(unit.id) }));


export function useKpiCatalogues() {
  const [modules, setModules] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [unitMaster, setUnitMaster] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [ghgDatabases, setGhgDatabases] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [modRes, fyRes, unitRes, kwRes, dbRes, fwRes, indRes] = await Promise.all([
        getModules(),
        getFinancialYears(),
        getUnitMaster(),
        getKeywords(),
        getGhgDatabases(),
        getFrameworks(),
        getIndustries(),
      ]);
      setModules(modRes?.data?.data || []);
      setFinancialYears(fyRes?.data?.data || []);
      setUnitMaster(normalizeUnits(unitRes?.data?.data || unitRes?.data || []));
      setKeywords(kwRes?.data?.data || []);
      setGhgDatabases(dbRes?.data?.data || []);
      setFrameworks(fwRes?.data?.data || []);
      setIndustries(indRes?.data?.data || []);
    } catch (e) {
      setError(e?.message || "Could not load the KPI reference lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);


  const tagValuesByKey = useMemo(() => {
    const index = {};
    keywords.forEach(({ keyword_type: key, keyword_value: value }) => {
      if (!index[key]) index[key] = [];
      if (!index[key].includes(value)) index[key].push(value);
    });
    Object.keys(index).forEach((key) => index[key].sort((a, b) => a.localeCompare(b)));
    return index;
  }, [keywords]);

  const addKeyword = useCallback((keywordType, keywordValue, esgPillar = null) => {
    setKeywords((current) =>
      current.some((k) => k.keyword_type === keywordType && k.keyword_value === keywordValue)
        ? current
        : [...current, { keyword_type: keywordType, keyword_value: keywordValue, esg_pillar: esgPillar }],
    );
  }, []);

  const modulePillars = useMemo(() => modulePillarsOf(keywords), [keywords]);

  /** Module tags in the vocabulary that no pillar covers yet, each spelling group counted once. */
  const unassignedModuleCount = useMemo(() => {
    const unassigned = new Set();
    keywords.forEach((keyword) => {
      const tag = normalizeTagText(keyword.keyword_value);
      if (isModuleKeyword(keyword.keyword_type) && !modulePillars[tag]) unassigned.add(tag);
    });
    return unassigned.size;
  }, [keywords, modulePillars]);

  const setModulePillar = useCallback((moduleTag, esgPillar) => {
    setKeywords((current) => withModulePillar(current, moduleTag, esgPillar));
  }, []);

  const tagKeys = useMemo(() => {
    const fromKeywords = Object.keys(tagValuesByKey);
    return [...new Set([...TAG_PATH_KEYS, ...fromKeywords])];
  }, [tagValuesByKey]);

  return {
    modules,
    financialYears,
    unitMaster,
    ghgDatabases,
    frameworks,
    industries,
    tagKeys,
    tagValuesByKey,
    addKeyword,
    modulePillars,
    unassignedModuleCount,
    setModulePillar,
    loading,
    error,
    reload: load,
  };
}
